import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { DeleteOutlined, FormOutlined, RollbackOutlined } from "@ant-design/icons";

// Internal Imports
import ButtonGrid from "../../../common/components/ButtonGrid/ButtonGrid";
import { COLORS } from "../../../common/constants/colors";
import { Mode } from "../../../common/types/AllTypes";
import { useForm } from "../../../contexts/form";
import IColumn from "../../../interfaces/IColumn";
import { IFormDetail } from "../../../interfaces/IForm";
import { TypeColumn } from "../../../common/enums/AllEnums";
import globalStyle from "../../../common/styles/globalStyle.module.css";
import GroupRow from "../../../common/components/GroupRow/GroupRow";
import MarginInput from "../../../common/components/MarginInput/MarginInput";
import SelectBox from "../../../common/components/SelectBox/SelectBox";
import EditTextForm from "../../../common/components/EditTextForm/EditTextForm";
import Paint from "../../../common/components/Paint/Paint";
import { makeTextPlain, openNotification, returnOrdenSelection } from "../../../common/constants/Utilities";
import useListTypeDetail from "../../../hooks/useListTypeDetail";
import useListTypeColumn from "../../../hooks/useListTypeColumn";
import useListAttribute from "../../../hooks/useListAttribute";
import DetailTable from "../../../common/components/DetailTable/DetailTable";
import OutLineButton from "../../../common/components/OutLineButton/OutLineButton";

interface Props {
    setCanGoNext: React.Dispatch<React.SetStateAction<boolean>>;
    setCanGoBack: React.Dispatch<React.SetStateAction<boolean>>;
    mode: Mode;
}


const StepFour = ({ setCanGoNext, setCanGoBack, mode }: Props) => {
    // States
    const { formQuestion, setFormQuestion, loadingSubmit, setStatusFormQuestion } = useForm();
    const [detailUpdate, setDetailUpdate] = useState<IFormDetail>();
    const [detailPosition, setDetailPosition] = useState<number | null>(null);
    const [listFormDetail, setListFormDetail] = useState<IFormDetail[]>([]);
    const { listTypeDetail, loadingTypesDetail } = useListTypeDetail();
    const { listTypeColumn, loadingTypesColumn } = useListTypeColumn();
    const { listAttribute, loadingAttr } = useListAttribute();


    // Const
    const columns: IColumn[] = [
        {
            title: 'Grupo',
            dataIndex: 'nombre',
        },
        {
            title: 'Orden',
            dataIndex: 'orden',
        },

        {
            title: "",
            dataIndex: "",
            render: (detail: IFormDetail) => (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <ButtonGrid color={COLORS.black_text_color4} active={(loadingSubmit || (detail.nombre !== detailUpdate?.nombre && !!detailUpdate)) ? false : true} icon={detailUpdate?.nombre === detail.nombre ? <RollbackOutlined /> : <FormOutlined />} method={() => selectDetail(detail)} />
                    <nav>&nbsp;&nbsp;</nav>
                    <ButtonGrid color={COLORS.black_text_color4} active={(loadingSubmit || !!detailUpdate) ? false : true} icon={<DeleteOutlined />} method={() => removeDetail(detail.nombre)} />
                </div>
            )
        }
    ];

    // Methods
    const validations = (values: any) => {
        const errors: any = {};

        if (!values.nombre) {
            errors.nombre = 'Requerido';
        }

        if (!values.orden) {
            errors.orden = 'Requerido';
        }

        if (!values.detalleTipo) {
            errors.detalleTipo = 'Requerido';
        }

        if (!values.columnaTipo) {
            errors.columnaTipo = 'Requerido';
        } else if (values.columnaTipo === TypeColumn.AtributoListado.toString() && !values.columnaValor) {
            errors.columnaValor = 'Requerido';
        }

        return errors;
    }

    const clearForm = () => {
        FormQuestionFormik.resetForm();
    }

    const addDetail = () => {
        if (listFormDetail.some(det => makeTextPlain(det.nombre) === makeTextPlain(FormQuestionFormik.values.nombre))) {
            openNotification('error', 'Ya existe', `El detalle ${FormQuestionFormik.values.nombre} ya existe`);
        } else {
            const newFromDt: IFormDetail = {
                formularioDetalleID: 0,
                formularioID: formQuestion!.formularioID,
                nombre: FormQuestionFormik.values.nombre,
                detalleTipo: parseInt(FormQuestionFormik.values.detalleTipo),
                detalleTipoNombre: listTypeDetail.find(det => det.key === parseInt(FormQuestionFormik.values.detalleTipo))!.value,
                columnaTipo: parseInt(FormQuestionFormik.values.columnaTipo),
                columnaTipoNombre: listTypeColumn.find(typeCl => typeCl.key === parseInt(FormQuestionFormik.values.columnaTipo))!.value,
                columnaValor: FormQuestionFormik.values.columnaValor,
                orden: parseInt(FormQuestionFormik.values.orden)
            }

            let listInternal = formQuestion!.formularioDetalle!;
            listInternal.push(newFromDt);

            setFormQuestion({ ...formQuestion!, formularioDetalle: listInternal! });
            clearForm();
        }
    }

    const selectDetail = (detail: IFormDetail) => {
        if (!detailUpdate) {
            setDetailUpdate(detail);
            FormQuestionFormik.setValues({
                nombre: detail.nombre,
                orden: detail.orden.toString(),
                detalleTipo: detail.detalleTipo.toString(),
                columnaTipo: detail.columnaTipo.toString(),
                columnaValor: detail.columnaValor || ''
            });
            setDetailPosition(listFormDetail.findIndex(item => item.nombre === detail.nombre))
        } else {
            setDetailUpdate(undefined);
            clearForm();
            setDetailPosition(null);
        }
    }

    const updateDetail = () => {
        const found = listFormDetail.findIndex(det => makeTextPlain(det.nombre) === makeTextPlain(FormQuestionFormik.values.nombre));

        if (found !== -1 && found !== detailPosition) {
            openNotification('error', 'Ya existe', `El detalle ${FormQuestionFormik.values.nombre} ya existe`);
        } else {
            let internal: IFormDetail = {
                formularioDetalleID: detailUpdate?.formularioDetalleID!,
                formularioID: detailUpdate!.formularioID,
                nombre: FormQuestionFormik.values.nombre,
                detalleTipo: parseInt(FormQuestionFormik.values.detalleTipo),
                detalleTipoNombre: listTypeDetail.find(det => det.key === parseInt(FormQuestionFormik.values.detalleTipo))!.value,
                columnaTipo: parseInt(FormQuestionFormik.values.columnaTipo),
                columnaTipoNombre: listTypeColumn.find(typeCl => typeCl.key === parseInt(FormQuestionFormik.values.columnaTipo))!.value,
                columnaValor: FormQuestionFormik.values.columnaValor,
                orden: parseInt(FormQuestionFormik.values.orden)
            }

            let listInternal = formQuestion!.formularioDetalle!;
            let index = listInternal.findIndex(x => x.nombre === detailUpdate!.nombre);

            listInternal[index] = internal!;

            setFormQuestion({ ...formQuestion!, formularioDetalle: [...listInternal!] });

            // Finally
            setDetailUpdate(undefined);
            clearForm();
        }
    }

    const removeDetail = (detailName: string) => {
        const list = listFormDetail.filter(det => det.nombre !== detailName);
        setFormQuestion({ ...formQuestion!, formularioDetalle: [...list!] });
    }

    // Form
    const FormQuestionFormik = useFormik({
        initialValues: { nombre: "", orden: "", detalleTipo: "", columnaTipo: "", columnaValor: "" },
        validate: validations,
        onSubmit: () => !!detailUpdate ? updateDetail() : addDetail(),
    })


    // Effects
    useEffect(() => {
        if (!!formQuestion && formQuestion?.formularioDetalle!.length > 0) {
            setListFormDetail([...formQuestion?.formularioDetalle!]);
        } else {
            setListFormDetail([]);
        }
    }, [formQuestion]);

    useEffect(() => {
        if (!!detailUpdate) {
            setCanGoNext(false);
            setCanGoBack(false)
            setStatusFormQuestion(false);
        } else {
            setCanGoNext(true);
            setCanGoBack(true);
            setStatusFormQuestion(true);
        }
    }, [detailUpdate]);



    return (
        <div style={{ marginTop: 20 }}>
            <GroupRow>
                <MarginInput width="45%">
                    <label>Nombre:</label>
                    <EditTextForm
                        value={FormQuestionFormik.values.nombre}
                        onChange={FormQuestionFormik.handleChange}
                        backgroundcolor={COLORS.white_color2(1)}
                        name={"nombre"}
                        placeholder={"Nombre"}
                        borderColor={COLORS.black_text_color3}
                        textColor={COLORS.black_text_color1}
                        active={!loadingSubmit}
                        type={'text'}
                    />
                    <p className={globalStyle.error}>
                        <Paint color={COLORS.red_error2}>{FormQuestionFormik.errors.nombre && FormQuestionFormik.errors.nombre}</Paint>
                    </p>
                </MarginInput>

                <MarginInput width="45%">
                    <label>Orden:</label>
                    <SelectBox
                        value={FormQuestionFormik.values.orden}
                        placeholder={"Orden"}
                        disabled={loadingSubmit}
                        allowClear={false}
                        loading={loadingTypesDetail || loadingTypesColumn}
                        onChange={(value: string) => FormQuestionFormik.setFieldValue("orden", value)}
                        options={returnOrdenSelection(detailUpdate?.orden.toString() || '', formQuestion?.formularioDetalle)}
                        backgroundColor={COLORS.white_color2(1)}
                        borderColor={COLORS.black_text_color3}
                        textColor={COLORS.black_text_color1}
                    />
                    <p className={globalStyle.error}>
                        <Paint color={COLORS.red_error2}>{FormQuestionFormik.errors.orden && FormQuestionFormik.errors.orden}</Paint>
                    </p>
                </MarginInput>
            </GroupRow>

            <GroupRow>
                <MarginInput width="45%">
                    <label>Detalle tipo:</label>
                    <SelectBox
                        value={FormQuestionFormik.values.detalleTipo}
                        placeholder={"Detalle tipo"}
                        disabled={loadingSubmit}
                        allowClear={false}
                        loading={loadingTypesDetail}
                        onChange={(value: string) => FormQuestionFormik.setFieldValue('detalleTipo', value)}
                        options={listTypeDetail.map(typeDt => ({ key: typeDt.value, value: typeDt.key.toString(), text: typeDt.value }))}
                        backgroundColor={COLORS.white_color2(1)}
                        borderColor={COLORS.black_text_color3}
                        textColor={COLORS.black_text_color1}
                    />
                    <p className={globalStyle.error}>
                        <Paint color={COLORS.red_error2}>{FormQuestionFormik.errors.detalleTipo && FormQuestionFormik.errors.detalleTipo}</Paint>
                    </p>
                </MarginInput>

                <MarginInput width="45%">
                    <label>Columna tipo:</label>
                    <SelectBox
                        value={FormQuestionFormik.values.columnaTipo}
                        placeholder={"Columna tipo"}
                        disabled={loadingSubmit}
                        allowClear={false}
                        loading={loadingTypesColumn}
                        onChange={(value: string) => { FormQuestionFormik.setFieldValue('columnaTipo', value); FormQuestionFormik.setFieldValue('columnaValor', ''); }}
                        options={listTypeColumn.map(typeCl => ({ key: typeCl.value, value: typeCl.key.toString(), text: typeCl.value }))}
                        backgroundColor={COLORS.white_color2(1)}
                        borderColor={COLORS.black_text_color3}
                        textColor={COLORS.black_text_color1}
                    />
                    <p className={globalStyle.error}>
                        <Paint color={COLORS.red_error2}>{FormQuestionFormik.errors.columnaTipo && FormQuestionFormik.errors.columnaTipo}</Paint>
                    </p>
                </MarginInput>
            </GroupRow>

            {FormQuestionFormik.values.columnaTipo === TypeColumn.AtributoListado.toString() &&
                <GroupRow>
                    <MarginInput width="100%">
                        <label>Detalle tipo:</label>
                        <SelectBox
                            value={FormQuestionFormik.values.columnaValor}
                            placeholder={"Detalle valor"}
                            disabled={loadingSubmit}
                            allowClear={false}
                            loading={loadingAttr}
                            onChange={(value: string) => FormQuestionFormik.setFieldValue('columnaValor', value)}
                            options={listAttribute.filter(x => x.departamentoGUID === formQuestion?.departamentoGUID).map(atrr => ({ key: atrr.atributoGUID, value: atrr.atributoGUID, text: atrr.nombre }))}
                            backgroundColor={COLORS.white_color2(1)}
                            borderColor={COLORS.black_text_color3}
                            textColor={COLORS.black_text_color1}
                        />
                        <p className={globalStyle.error}>
                            <Paint color={COLORS.red_error2}>{FormQuestionFormik.errors.columnaValor && FormQuestionFormik.errors.columnaValor}</Paint>
                        </p>
                    </MarginInput>
                </GroupRow>
            }


            <div>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                    <MarginInput width="40%">
                        <OutLineButton color={!!detailUpdate ? COLORS.white_color1 : COLORS.blue_active} backgroundColor={!!detailUpdate ? COLORS.blue_main(1) : COLORS.transparent_color1} text={!!detailUpdate ? "Modificar Detalle" : "Añadir Detalle"} method={FormQuestionFormik.handleSubmit} />
                    </MarginInput>
                </div>
                <DetailTable
                    props={{ rowKey: (record: IFormDetail) => record.nombre }}
                    dataSource={listFormDetail}
                    columns={columns}
                    loading={false}
                    paginationActive={!detailUpdate}
                />
            </div>
        </div>
    );
}

export default StepFour;